import { get, post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { apiPost } from '../utils/api';

/**
 * Fetch user attributes for a specific user
 * @param {string} username - The username to fetch attributes for
 * @returns {Promise<Object>} The user's attributes
 */
export const fetchUserAttributes = async (username) => {
  try {
    // Call the API using Amplify's API category
    const result = await get({
      apiName: 'coreuser',
      path: `/core/userattributes/${username}`
    }).response;

    console.log('User attributes API Response:', result);
    
    if (result.body === '') {
      console.error('Empty response received from user attributes API');
      throw new Error('Empty response from server');
    }

    const attributesData = await result.body.json();
    console.log('Parsed user attributes:', attributesData);
    return attributesData;
  } catch (error) {
    console.error('Error fetching user attributes:', {
      error,
      message: error.message,
      status: error.status,
      response: error.response
    });
    throw error;
  }
};

/**
 * Update user attributes for a specific user
 * @param {string} username - The username to update attributes for
 * @param {Object} attributes - The attributes to update
 * @returns {Promise<Object>} The response from the update operation
 */
export const updateUserAttributes = async (username, attributes) => {
  try {
    // Use Amplify's API category which will automatically handle authentication
    const result = await post({
      apiName: 'coreuser',
      path: `/core/userattributes/${username}`,
      options: {
        body: attributes
      }
    }).response;

    console.log('Update user attributes API Response:', result);
    
    if (result.body === '') {
      console.error('Empty response received from user attributes API');
      throw new Error('Empty response from server');
    }

    const responseData = await result.body.json();
    console.log('Update attributes response:', responseData);
    return responseData;
  } catch (error) {
    console.error('Error updating user attributes:', {
      error,
      message: error.message,
      status: error.status,
      response: error.response
    });
    throw error;
  }
};

/**
 * Fetch the list of available sites from the Amplify domain configuration.
 * Only needed by admins editing country users.
 * @returns {Promise<string[]>} Array of domain names
 */
export const fetchAvailableSites = async () => {
  try {
    const result = await get({
      apiName: 'coreuser',
      path: '/core/userattributes/available-sites'
    }).response;

    if (result.body === '') {
      throw new Error('Empty response from server');
    }

    const data = await result.body.json();
    return data.sites || [];
  } catch (error) {
    console.error('Error fetching available sites:', error);
    throw error;
  }
};

/**
 * Set initial user attributes after user creation
 * @param {string} username - The username to set attributes for
 * @param {Object} attributes - The initial attributes to set
 * @returns {Promise<Object>} The response from the set operation
 */
export const setInitialUserAttributes = async (username, attributes) => {
  try {
    // Ensure creationDate is set if not already present
    if (!attributes.creationDate) {
      attributes.creationDate = new Date().toISOString();
    }

    return await updateUserAttributes(username, attributes);
  } catch (error) {
    console.error('Error setting initial user attributes:', error);
    throw error;
  }
}; 